import React, { useEffect, useState } from 'react';
import { Form, Upload as UploadAntd, UploadFile } from 'antd';
import ImgCrop from 'antd-img-crop';
import cn from 'classnames';
import { ReactComponent as IconPlus } from 'static/icons/add.svg';
import { ReactComponent as IconLoading } from 'static/icons/loading.svg';
import { ReactComponent as IconBin } from 'static/icons/bin.svg';
import { RcFile } from 'antd/es/upload';
import { beforeUpload, extractFileObject, getBase64 } from './utils';
import { UploadProps } from './types';
import './style.scss';

export const Upload: React.FC<UploadProps> = ({
  name,
  label,
  language,
  crop,
  rules,
  className,
  formItemProps,
  ...props
}) => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const form = Form.useFormInstance();

  const uploadElement = (
    <UploadAntd
      listType="picture-card"
      fileList={fileList}
      beforeUpload={beforeUpload(form, setError, setLoading, name, language === 'es', true)}
      maxCount={1}
      showUploadList={false}
      itemRender={() => null}
      customRequest={async ({ file, onSuccess }) => {
        if (!(file instanceof File)) return;

        setLoading(true);
        try {
          const preview = await getBase64(file);

          const rcFile: RcFile = {
            ...file,
            uid: (file as RcFile).uid || Date.now().toString(),
            lastModifiedDate: file.lastModified ? new Date(file.lastModified) : new Date(),
          };

          const updatedFile: UploadFile = {
            uid: rcFile.uid || Date.now().toString(),
            name: rcFile.name,
            status: 'done',
            originFileObj: rcFile,
            thumbUrl: preview,
          };

          setFileList([updatedFile]);

          const actualFile = extractFileObject(file);

          if (form) {
            form.setFieldsValue({
              [name]: actualFile,
            });
          }

          onSuccess?.('ok');
        } catch (err) {
          setError(language === 'es' ? 'Error de vista previa' : 'Preview error');
        } finally {
          setLoading(false);
        }
      }}
      disabled={loading}
      {...props}
    >
      {fileList.length >= 1 ? (
        <div className="custom-upload-preview">
          <img src={fileList[0].thumbUrl || fileList[0].url} alt="preview" className="custom-upload-img" />
          <button
            className="custom-upload-hover"
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setFileList([]);
              form.setFieldsValue({ [name]: undefined });
            }}
          >
            <IconBin />
          </button>
        </div>
      ) : (
        <button className="upload-button" type="button">
          {loading ? <IconLoading /> : <IconPlus />}
          {!loading && <div>{language === 'es' ? 'Subir' : 'Upload'}</div>}
        </button>
      )}
    </UploadAntd>
  );

  useEffect(() => {
    const fileValue = form.getFieldValue(name);
    const file = extractFileObject(fileValue);
    if (!file) return;

    const objectUrl = URL.createObjectURL(file);
    setFileList([
      {
        uid: '-1',
        name: file.name,
        status: 'done',
        url: objectUrl,
        originFileObj: { ...file, lastModifiedDate: new Date(), uid: Date.now().toString() },
      },
    ]);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [form, name]);

  return (
    <div className={cn('form-item_upload-avatar', className)}>
      <Form.Item
        name={name}
        label={label}
        rules={rules}
        labelCol={{ span: 24 }}
        help={error}
        validateStatus={error ? 'error' : undefined}
        {...formItemProps}
        valuePropName="file"
      >
        {crop ? (
          <ImgCrop
            modalTitle={language === 'es' ? 'Recortar imagen' : 'Crop image'}
            modalOk={language === 'es' ? 'Subir' : 'Upload'}
            modalCancel={language === 'es' ? 'Cancelar' : 'Cancel'}
            cropShape="rect"
            aspectSlider
            beforeCrop={beforeUpload(form, setError, setLoading, name, language === 'es')}
          >
            {uploadElement}
          </ImgCrop>
        ) : (
          uploadElement
        )}
      </Form.Item>
    </div>
  );
};
